// ─────────────────────────────────────────────────────────────────────────────
//  AlertDetail.jsx
//  Expanded detail panel rendered inside ManagedAlertCard
//  Includes: driver strip, type-context sections, audit trail, action buttons
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState, useRef, useCallback } from "react";
import {
  ALERT_CONFIGS,
  ALERT_BORDER,
  KPI_TYPES,
  TRAIL_COLOR,
  TRAIL_LABEL,
  getDriver,
  getExtra,
  getInitials,
  formatEventTime,
} from "./notificationConstants";

/* ── tiny reusable info cell ── */
export const InfoField = ({ label, value, highlight, classes }) => (
  <div className={classes.infoField}>
    <div className={classes.infoLbl}>{label}</div>
    <div className={highlight ? classes.infoValRed : classes.infoVal}>
      {value || "—"}
    </div>
  </div>
);

/* ── type-specific context section ── */
const TypeContext = ({ n, classes }) => {
  const type = n.notificationType;
  const extra = getExtra(type);

  if (type === "OVER_SPEED_ALERT")
    return (
      <>
        <div className={classes.sectionDivider}>⚡ Overspeed Details</div>
        <div className={classes.infoGrid3}>
          <InfoField
            label="🚀 Recorded Speed"
            value={extra.recordedSpeed}
            highlight
            classes={classes}
          />
          <InfoField
            label="🚦 Speed Limit"
            value={extra.speedLimit}
            classes={classes}
          />
          <InfoField
            label="⬆ Excess"
            value={extra.excess}
            highlight
            classes={classes}
          />
        </div>
        <div className={classes.infoGrid2}>
          <InfoField
            label="⏱ Duration"
            value={extra.duration}
            classes={classes}
          />
          <InfoField
            label="📏 Distance"
            value={extra.distance}
            classes={classes}
          />
        </div>
      </>
    );

  if (type === "OUT_ALERT")
    return (
      <>
        <div className={classes.sectionDivider}>🔒 Theft Alert Details</div>
        <div className={classes.anomalyBanner}>
          <div
            style={{
              fontSize: "0.63rem",
              fontWeight: 800,
              color: "#C62828",
              textTransform: "uppercase",
              letterSpacing: ".4px",
              marginBottom: 8,
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <span style={{ fontSize: 14 }}>⚠️</span> Anomaly Detected
          </div>
          <div className={classes.infoGrid2}>
            <InfoField
              label="🔑 Last Ignition"
              value={extra.lastIgnition}
              highlight
              classes={classes}
            />
            <InfoField
              label="🚗 Movement"
              value={extra.movement}
              highlight
              classes={classes}
            />
            <InfoField
              label="🔋 Battery Voltage"
              value={extra.batteryVoltage}
              classes={classes}
            />
            <InfoField
              label="👤 Last Known Driver"
              value={extra.lastKnownDriver}
              classes={classes}
            />
          </div>
        </div>
      </>
    );

  if (type === "TAMPERING_ALERT")
    return (
      <>
        <div className={classes.sectionDivider}>🔌 Tampering Details</div>
        <div className={classes.infoGrid2}>
          <InfoField
            label="⚠ Disconnect Time"
            value={extra.disconnectTime}
            highlight
            classes={classes}
          />
          <InfoField
            label="📍 Disconnect Loc"
            value={extra.disconnectLoc}
            classes={classes}
          />
          <InfoField
            label="✅ Reconnect Time"
            value={extra.reconnectTime}
            classes={classes}
          />
          <InfoField
            label="📍 Reconnect Loc"
            value={extra.reconnectLoc}
            classes={classes}
          />
        </div>
        {extra.movedInBetween && (
          <div className={classes.movedBanner}>
            🚨 Vehicle moved <strong>{extra.distMoved}</strong> while device was
            disconnected (gap: {extra.gapDuration})
          </div>
        )}
      </>
    );

  if (type === "UNAUTHORIZED_STOP_ALERT")
    return (
      <>
        <div className={classes.tripBanner}>
          <div
            style={{
              fontSize: "0.63rem",
              fontWeight: 800,
              color: "#2E7D32",
              textTransform: "uppercase",
              letterSpacing: ".4px",
              marginBottom: 8,
            }}
          >
            🚦 Trip Context
          </div>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
            <div>
              <div
                style={{
                  fontSize: "0.6rem",
                  fontWeight: 700,
                  color: "#78909C",
                }}
              >
                Trip ID
              </div>
              <div
                style={{
                  fontSize: "0.72rem",
                  fontWeight: 800,
                  color: "#1A1A2E",
                }}
              >
                {extra.tripId || "—"}
              </div>
            </div>
            <div>
              <div
                style={{
                  fontSize: "0.6rem",
                  fontWeight: 700,
                  color: "#78909C",
                }}
              >
                From
              </div>
              <div
                style={{
                  fontSize: "0.72rem",
                  fontWeight: 800,
                  color: "#1A1A2E",
                }}
              >
                {extra.tripFrom || "—"}
              </div>
            </div>
            <div>
              <div
                style={{
                  fontSize: "0.6rem",
                  fontWeight: 700,
                  color: "#78909C",
                }}
              >
                To
              </div>
              <div
                style={{
                  fontSize: "0.72rem",
                  fontWeight: 800,
                  color: "#1A1A2E",
                }}
              >
                {extra.tripTo || "—"}
              </div>
            </div>
          </div>
        </div>
        <div className={classes.sectionDivider}>
          🛑 Unauthorized Stop Details
        </div>
        <div className={classes.infoGrid2}>
          <InfoField
            label="⏱ Stop Duration"
            value={extra.stopDuration}
            highlight
            classes={classes}
          />
          <InfoField
            label="📋 Rule Threshold"
            value={extra.ruleThreshold}
            classes={classes}
          />
          <InfoField
            label="📍 Stop Location"
            value={extra.stopLoc}
            classes={classes}
          />
          <InfoField
            label="🔑 Engine · Speed"
            value={
              extra.engineStatus && extra.speed
                ? `${extra.engineStatus} · ${extra.speed}`
                : "—"
            }
            classes={classes}
          />
        </div>
      </>
    );

  if (type === "ROUTE_DEVIATION_ALERT")
    return (
      <>
        <div className={classes.tripBanner}>
          <div
            style={{
              fontSize: "0.63rem",
              fontWeight: 800,
              color: "#2E7D32",
              textTransform: "uppercase",
              letterSpacing: ".4px",
              marginBottom: 8,
            }}
          >
            🚦 Trip Context
          </div>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
            <div>
              <div
                style={{
                  fontSize: "0.6rem",
                  fontWeight: 700,
                  color: "#78909C",
                }}
              >
                Trip ID
              </div>
              <div
                style={{
                  fontSize: "0.72rem",
                  fontWeight: 800,
                  color: "#1A1A2E",
                }}
              >
                {extra.tripId || "—"}
              </div>
            </div>
            <div>
              <div
                style={{
                  fontSize: "0.6rem",
                  fontWeight: 700,
                  color: "#78909C",
                }}
              >
                From → To
              </div>
              <div
                style={{
                  fontSize: "0.72rem",
                  fontWeight: 800,
                  color: "#1A1A2E",
                }}
              >
                {extra.tripFrom} → {extra.tripTo}
              </div>
            </div>
            <div>
              <div
                style={{
                  fontSize: "0.6rem",
                  fontWeight: 700,
                  color: "#78909C",
                }}
              >
                Status
              </div>
              <div
                style={{
                  fontSize: "0.72rem",
                  fontWeight: 800,
                  color: "#1976D2",
                }}
              >
                {extra.currentStatus || "—"}
              </div>
            </div>
          </div>
        </div>
        <div className={classes.sectionDivider}>🗺️ Route Deviation Details</div>
        <div className={classes.infoGrid2}>
          <InfoField
            label="📐 Assigned Route"
            value={extra.assignedRoute}
            classes={classes}
          />
          <InfoField
            label="📍 Deviation Point"
            value={extra.deviationPoint}
            highlight
            classes={classes}
          />
          <InfoField
            label="📏 Deviation Distance"
            value={extra.deviationDist}
            highlight
            classes={classes}
          />
          <InfoField
            label="⏱ Deviation Duration"
            value={extra.deviationDuration}
            classes={classes}
          />
        </div>
      </>
    );

  if (type === "USER_IN_ALERT" || type === "USER_OUT_ALERT")
    return (
      <>
        <div className={classes.sectionDivider}>📍 Geofence Details</div>
        <div
          className={classes.contextBanner}
          style={{ borderColor: "#CE93D8", background: "#F3E5F5" }}
        >
          <div className={classes.contextTitle} style={{ color: "#7B1FA2" }}>
            {type === "USER_IN_ALERT"
              ? "📥 Geofence Entry"
              : "📤 Geofence Exit"}
          </div>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            {n.geofenceName && (
              <div>
                <div
                  style={{
                    fontSize: "0.6rem",
                    fontWeight: 700,
                    color: "#78909C",
                  }}
                >
                  Zone
                </div>
                <div
                  style={{
                    fontSize: "0.72rem",
                    fontWeight: 800,
                    color: "#1A1A2E",
                  }}
                >
                  {n.geofenceName}
                </div>
              </div>
            )}
            {n.latitude && (
              <div>
                <div
                  style={{
                    fontSize: "0.6rem",
                    fontWeight: 700,
                    color: "#78909C",
                  }}
                >
                  Coords
                </div>
                <div
                  style={{
                    fontSize: "0.72rem",
                    fontWeight: 800,
                    color: "#1A1A2E",
                  }}
                >
                  {parseFloat(n.latitude).toFixed(5)},{" "}
                  {parseFloat(n.longitude).toFixed(5)}
                </div>
              </div>
            )}
          </div>
        </div>
      </>
    );

  /* default fallback */
  return (
    <>
      <div className={classes.sectionDivider}>ℹ️ Details</div>
      <div className={classes.infoGrid2}>
        <InfoField
          label="📍 Coordinates"
          value={
            n.latitude
              ? `${parseFloat(n.latitude).toFixed(4)}, ${parseFloat(n.longitude).toFixed(4)}`
              : "N/A"
          }
          classes={classes}
        />
        <InfoField
          label="💬 Message"
          value={n.message || "—"}
          classes={classes}
        />
      </div>
    </>
  );
};

/* ── Main AlertDetail component ── */
const AlertDetail = React.memo(({ n, onAction, onMapClick, classes }) => {
  const [remark, setRemark] = useState("");
  const [error, setError] = useState(false);
  const taRef = useRef(null);

  const isOpen = n.status === "open";
  const isRejected = n.status === "rejected";
  const lastTrail = n.trail?.[n.trail.length - 1];
  const kpiCfg = KPI_TYPES.find((k) => k.type === n.notificationType);
  const canImmobilize = kpiCfg?.canImmobilize && n.relay;

  const driver = getDriver(n.deviceName);
  const driverInitials = getInitials(driver.name);

  const handleAction = useCallback(
    (act) => {
      const trimmed = remark.trim();
      if (!trimmed) {
        setError(true);
        taRef.current?.focus();
        return;
      }
      setError(false);
      onAction(n._id, act, trimmed);
    },
    [remark, n._id, onAction],
  );

  const openMap = (e) => {
    e.stopPropagation();
    onMapClick({
      lat: n.latitude ?? 28.6139,
      lng: n.longitude ?? 77.209,
      label: `${(ALERT_CONFIGS[n.notificationType] || ALERT_CONFIGS.default).label} — ${n.deviceName || ""}`,
      notificationType: n.notificationType,
      alertTime: n.eventTime,
    });
  };

  return (
    <div className={classes.detailPanel} onClick={(e) => e.stopPropagation()}>
      {/* ── Driver strip ── */}
      <div className={classes.driverStrip}>
        <div className={classes.driverAvatar}>{driverInitials}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className={classes.driverName}>👤 {driver.name}</div>
          <div className={classes.driverMeta}>
            Lic: {driver.lic} · Exp: {driver.exp}
          </div>
        </div>
        <button
          className={classes.callBtn}
          onClick={(e) => e.stopPropagation()}
          title={`Call ${driver.phone}`}
          onMouseEnter={(e) => (e.currentTarget.style.background = "#0D47A1")}
          onMouseLeave={(e) => (e.currentTarget.style.background = "#1565C0")}
        >
          📞 Call
        </button>
      </div>

      {/* ── Core grid ── */}
      {/* <div className={classes.infoGrid3}>
        <InfoField label="🚗 Vehicle"   value={n.deviceName || "—"} classes={classes} />
        <InfoField label="📍 Location"  value={n.latitude ? `${parseFloat(n.latitude).toFixed(4)}, ${parseFloat(n.longitude).toFixed(4)}` : "N/A"} classes={classes} />
        <InfoField label="🕐 Time"      value={formatEventTime(n.eventTime)} classes={classes} />
      </div> */}

      {/* ── Type-specific context ── */}
      {/* <TypeContext n={n} classes={classes} /> */}

      {/* ── Audit trail ── */}
      {n.trail?.length > 0 && (
        <>
          <div className={classes.sectionDivider}>📋 Action History</div>
          <div className={classes.trail}>
            {n.trail.map((t, i) => (
              <div key={i} className={classes.trailRow}>
                <div
                  className={classes.trailDot}
                  style={{ background: TRAIL_COLOR[t.act] || "#1976D2" }}
                />
                <div className={classes.trailText}>
                  <strong>{t.by}</strong> ({t.role}) —{" "}
                  {TRAIL_LABEL[t.act] || "Actioned"} at {t.ts}
                  {" · "}
                  <em style={{ color: "#78909C" }}>"{t.remark}"</em>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* ── Actions ── */}
      {isOpen ? (
        // <>
        //   <div className={classes.sectionDivider}>⚡ Take Action</div>
        //   <div className={classes.remarksWrap}>
        //     <div className={classes.remarksLbl}>
        //       Add Remarks (Required) <span style={{ color: "#F44336" }}>*</span>
        //     </div>
        //     <textarea
        //       ref={taRef}
        //       className={classes.remarksTa}
        //       style={error ? { borderColor: "#F44336" } : undefined}
        //       rows={2}
        //       placeholder={
        //         error
        //           ? "⚠ Remarks required before taking action!"
        //           : "Enter your remarks before taking action…"
        //       }
        //       value={remark}
        //       // onChange={(e) => {
        //       //   setRemark(e.target.value);
        //       //   if (e.target.value.trim()) setError(false);
        //       // }}

        //       onChange={(e) => {
        //         const val = e.target.value;
        //         setRemark(val);
        //         if (val.trim()) setError(false);
        //         else if (val.length > 0) setError(true);
        //       }}
        //       onClick={(e) => e.stopPropagation()}
        //     />
        //   </div>
        //   <div className={classes.actionRow}>
        //     {/* <button className={`${classes.abtn} ${classes.btnTrack}`} onClick={openMap}>
        //       🗺️ Live Track
        //     </button> */}
        //     {canImmobilize && (
        //       <button
        //         className={`${classes.abtn} ${classes.btnImmob}`}
        //         onClick={(e) => {
        //           e.stopPropagation();
        //           handleAction("immob");
        //         }}
        //       >
        //         🚫 Immobilize
        //       </button>
        //     )}
        //     <button
        //       className={`${classes.abtn} ${classes.btnResolve}`}
        //       onClick={(e) => {
        //         e.stopPropagation();
        //         handleAction("resolve");
        //       }}
        //     >
        //       ✅ Resolve
        //     </button>
        //     <button
        //       className={`${classes.abtn} ${classes.btnReject}`}
        //       onClick={(e) => {
        //         e.stopPropagation();
        //         handleAction("reject");
        //       }}
        //     >
        //       ✕ Reject
        //     </button>
        //   </div>
        // </>
        <></>
      ) : (
        <div className={classes.actionRow} style={{ marginTop: 8 }}>
          {/* <button
            className={`${classes.abtn} ${classes.btnTrack}`}
            onClick={openMap}
          >
            🗺️ View Location
          </button>
          {isRejected ? (
            <span className={classes.rejectedTag}>
              ✕ Rejected by {lastTrail?.by || "Admin"}
            </span>
          ) : (
            <span className={classes.resolvedTag}>
              ✓ Resolved by {lastTrail?.by || "Admin"}
            </span>
          )} */}
        </div>
      )}
    </div>
  );
});

export default AlertDetail;
